import React from "react";
import {
  Button,
  DetailsWrapper,
  FormFieldSet,
  LoopFieldsSet,
  useFormHandler,
} from "../../../../../../components";
import { v4 as uuidv4 } from "uuid";
import { countriesCode } from "../../../../../../components/Validations/countryCodeData";

// Strips dash placeholders so edit fields start empty instead of showing "-"
// Preserves object structures ({label, value}) needed for select dropdowns 
// Strips dash placeholders so edit fields start empty instead of showing "-"
const nd = (val) => {
  if (val === null || val === undefined || val === "-") return "";
  if (typeof val === "object" && val !== null) {
    if (val.value === "-" || val.label === "-") return "";
    return val.value || val.label || "";
  }
  return val;
};

const EditClientDetails = ({ data, onClose, onSave }) => {
  const normalizePhoneValue = (rawValue) => {
    const value = String(rawValue || "").trim();
    if (!value) return "";
    const cleaned = value.replace(/[^\d+]/g, "");
    if (!cleaned) return "";
    if (cleaned.includes("+")) {
      return `+${cleaned.replace(/\+/g, "")}`;
    }
    return cleaned;
  };

  const extractCountryCodeFromPhone = (rawValue) => {
    const value = normalizePhoneValue(rawValue);
    if (!value.startsWith("+")) return "";

    const codes = countriesCode
      .map((c) => c.code)
      .sort((a, b) => b.length - a.length);

    return codes.find((code) => value.startsWith(code)) || "";
  };

  const parsePhoneData = (rawValue) => {
    if (!rawValue) return { countryCode: "", number: "" };

    if (typeof rawValue === "object") {
      return {
        countryCode: String(rawValue.countryCode || rawValue.code || "").trim(),
        number: String(rawValue.number || "").replace(/\D/g, ""),
      };
    }

    const normalized = normalizePhoneValue(rawValue);
    const countryCode = extractCountryCodeFromPhone(normalized);
    let numberPart = normalized;

    if (countryCode) {
      numberPart = normalized.substring(countryCode.length);
    } else if (numberPart.startsWith("+")) {
      numberPart = numberPart.substring(1);
    }

    return {
      countryCode,
      number: String(numberPart || "").replace(/\D/g, ""),
    };
  };

  const pocDetailsFromData = Array.isArray(data?.client?.pocDetails)
    ? data.client.pocDetails
    : [];

  const initialPocDetails =
    pocDetailsFromData.length > 0
      ? pocDetailsFromData.map((poc) => {
        const parsedPhone = parsePhoneData(poc?.pocPhone || poc?.phone);

        return {
          id: poc?.id || uuidv4(),
          name: nd(poc?.name || poc?.pocName),
          email: nd(poc?.email || poc?.pocEmail),
          phone: {
            code: parsedPhone.countryCode,
            number: parsedPhone.number,
          },
        };
      })
      : [
        {
          id: uuidv4(),
          name: "",
          email: "",
          phone: { code: "", number: "" },
        },
      ];

  const billingInvoiceFromData = Array.isArray(data?.client?.billingInvoice)
    ? data.client.billingInvoice
    : [];

  const initialBillingInvoice =
    billingInvoiceFromData.length > 0
      ? billingInvoiceFromData.map((invoice) => {
        const parsedPhone = parsePhoneData(invoice?.phone);

        return {
          id: invoice?.id || uuidv4(),
          name: nd(invoice?.name),
          email: nd(invoice?.email),
          phone: {
            code: parsedPhone.countryCode,
            number: parsedPhone.number,
          },
        };
      })
      : [
        {
          id: uuidv4(),
          name: "",
          email: "",
          phone: { code: "", number: "" },
        },
      ];

  const initialFormData = {
    legalBusinessName: nd(data?.client?.legalBusinessName || data?.client?.name),
    website: nd(data?.client?.website),
    addressLine1: nd(data?.client?.address?.addressLine1),
    addressLine2: nd(data?.client?.address?.addressLine2),
    city: nd(data?.client?.address?.city),
    state: nd(data?.client?.address?.state),
    country: nd(data?.client?.address?.country) || "United States",
    zipCode: nd(data?.client?.address?.zipCode),
    netTerms: data?.client?.netTerms ? String(data.client.netTerms) : "",
    federalId: nd(data?.client?.federalId ? String(data.client.federalId) : ""),
    pocDetails: initialPocDetails,
    billingInvoice: initialBillingInvoice,
  };

  const { formData, errors, handleChange, validateAllFields, setFormData } =
    useFormHandler(initialFormData, {
      legalBusinessName: {
        required: "Legal Business Name is required",
      },
      website: { type: "text" },
      addressLine1: {
        required: "Address Line 1 is required",
      },
      city: {
        required: "City is required",
      },
      state: {
        required: "State is required",
      },
      country: {
        required: "Country is required",
      },
      zipCode: {
        required: "Zip Code is required",
      },
      netTerms: {
        type: "select",
        required: "Net Terms is required",
      },
      federalId: { type: "text" },
      pocDetails: [
        {
          name: { type: "text", required: true },
          email: { type: "email", required: true },
          phone: { type: "phone", required: true },
        },
      ],
      billingInvoice: [
        {
          name: { type: "text", required: true },
          email: { type: "email", required: true },
          phone: { type: "phone", required: true },
        },
      ],
    });

  const onFieldChange = (field, value) => {
    const label = value?.label || value;
    const valueForValidation = typeof label === "number" ? String(label) : label;
    
    handleChange(field, valueForValidation);

    // Auto-update addressLine1 if city, state, country or zipCode changes
    if (["city", "state", "country", "zipCode"].includes(field)) {
      setFormData((prev) => {
        const currentData = { ...prev, [field]: valueForValidation };

        // Dependency resets: if country changes, clear state and city. If state changes, clear city.
        if (field === "country") {
          currentData.state = "";
          currentData.city = "";
        } else if (field === "state") {
          currentData.city = "";
        }

        const newAddr = [
          currentData.city,
          currentData.state,
          currentData.zipCode,
          currentData.country,
        ]
          .filter(Boolean)
          .join(", ");
        return {
          ...currentData,
          addressLine1: newAddr || "",
        };
      });
    }
  };

  const handleSave = () => {
    const isValid = validateAllFields();
    if (!isValid) return;
    if (onSave) onSave(formData);
  };

  const addPocDetails = () => {
    const newPoc = {
      id: uuidv4(),
      name: "",
      email: "",
      phone: { code: "", number: "" },
    };

    setFormData({
      ...formData,
      pocDetails: [...(formData.pocDetails || []), newPoc],
    });
  };

  const removePocDetails = (id) => {
    if ((formData.pocDetails || []).length <= 1) return;

    const updated = formData.pocDetails.filter((item) => item.id !== id);
    setFormData({
      ...formData,
      pocDetails: updated,
    });
  };

  const addBillingInvoice = () => {
    const newInvoice = {
      id: uuidv4(),
      name: "",
      email: "",
      phone: { code: "", number: "" },
    };

    setFormData({
      ...formData,
      billingInvoice: [...(formData.billingInvoice || []), newInvoice],
    });
  };

  const removeBillingInvoice = (id) => {
    if ((formData.billingInvoice || []).length <= 1) return;

    const updated = formData.billingInvoice.filter((item) => item.id !== id);
    setFormData({
      ...formData,
      billingInvoice: updated,
    });
  };

  return (
    <div className="flex flex-col gap-2 p-5">
      <div className="grid grid-cols-4 gap-5">
        <FormFieldSet
          title="Legal Business Name"
          type="text"
          placeholder="Legal Business Name"
          value={formData.legalBusinessName}
          onChange={(value) => handleChange("legalBusinessName", value)}
          error={errors.legalBusinessName}
        />
        <FormFieldSet
          title="Website"
          type="text"
          placeholder="Website"
          value={formData.website}
          onChange={(value) => handleChange("website", value)}
          error={errors.website}
        />
        <FormFieldSet
          title="Federal ID"
          type="text"
          placeholder="Federal ID"
          value={formData.federalId}
          onChange={(value) => handleChange("federalId", value)}
          error={errors.federalId}
        />
        <FormFieldSet
          title="Country"
          type="countries"
          placeholder="Country"
          value={formData.country || ""}
          onChange={(value) => onFieldChange("country", value)}
          error={errors.country}
        />
        <FormFieldSet
          title="State"
          type="states"
          parentValue={{ country: formData.country || "" }}
          value={formData.state || ""}
          placeholder="State"
          onChange={(value) => onFieldChange("state", value)}
          error={errors.state}
        />
        <FormFieldSet
          title="City"
          type="cities"
          parentValue={{ 
            country: formData.country || "", 
            state: formData.state || "" 
          }}
          placeholder="City"
          value={formData.city || ""}
          onChange={(value) => onFieldChange("city", value)}
          error={errors.city}
        />
        <FormFieldSet
          title="ZipCode"
          type="text"
          placeholder="ZipCode"
          value={formData.zipCode}
          onChange={(value) => onFieldChange("zipCode", value)}
          error={errors.zipCode}
        />
        <FormFieldSet
          title="Address line 1"
          type="text"
          placeholder="Address line 1"
          value={formData.addressLine1}
          onChange={(value) => handleChange("addressLine1", value)}
          error={errors.addressLine1}
        />
        <FormFieldSet
          title="Address line 2"
          type="text"
          placeholder="Address line 2"
          value={formData.addressLine2}
          onChange={(value) => handleChange("addressLine2", value)}
          error={errors.addressLine2}
        />
        <FormFieldSet
          title="Net Terms"
          type="select"
          options={[
            { label: "Net 30", value: 1 },
            { label: "Net 60", value: 2 },
            { label: "Net 90", value: 3 },
            { label: "Split(50-50)", value: 4 },
            { label: "Split(25-25-25-25)", value: 5 },
            { label: "Split(3 Equal Parts)", value: 6 },
          ]}
          placeholder="Net Terms"
          value={formData.netTerms}
          onChange={(value) => onFieldChange("netTerms", value)}
          error={errors.netTerms}
        />
      </div>
      <div className="grid grid-cols-1 gap-4 my-4">
        <DetailsWrapper title={"POC Details"}>
          <LoopFieldsSet
            title=""
            data={formData.pocDetails}
            onAdd={addPocDetails}
            onRemove={removePocDetails}
            addButtonTitle="Add POC Details"
            addRemoveButtonsPosition="inline"
            minItems={1}
            maxItems={4}
            gridCols="grid-cols-4"
            renderFields={(att, index) => (
              <>
                <FormFieldSet
                  title="Name"
                  placeholder="Enter POC Name"
                  value={att.name || ""}
                  onChange={(value) =>
                    handleChange(`pocDetails[${index}].name`, value)
                  }
                  error={errors.pocDetails?.[index]?.name}
                />
                <FormFieldSet
                  title="Email"
                  placeholder="Enter POC Email"
                  value={att.email || ""}
                  onChange={(value) =>
                    handleChange(`pocDetails[${index}].email`, value)
                  }
                  error={errors.pocDetails?.[index]?.email}
                />
                <FormFieldSet
                  title="Phone"
                  type="phone"
                  placeholder="Enter POC Phone"
                  code={att.phone?.code || ""}
                  value={att.phone?.number || ""}
                  onChange={(fullValue, details) => {
                    handleChange(`pocDetails[${index}].phone`, {
                      code: details.countryCode,
                      number: details.number,
                    });
                  }}
                  error={errors.pocDetails?.[index]?.phone}
                />
              </>
            )}
          />
        </DetailsWrapper>
        <DetailsWrapper title={"Billing and Invoicing"}>
          <LoopFieldsSet
            title=""
            data={formData.billingInvoice}
            onAdd={addBillingInvoice}
            onRemove={removeBillingInvoice}
            addButtonTitle="Add Billing Invoice"
            addRemoveButtonsPosition="inline"
            minItems={1}
            maxItems={4}
            gridCols="grid-cols-4"
            renderFields={(invoice, index) => (
              <>
                <FormFieldSet
                  title="Name"
                  placeholder="Enter Name"
                  value={invoice.name || ""}
                  onChange={(value) =>
                    handleChange(`billingInvoice[${index}].name`, value)
                  }
                  error={errors.billingInvoice?.[index]?.name}
                />
                <FormFieldSet
                  title="Email"
                  placeholder="Enter Email"
                  value={invoice.email || ""}
                  onChange={(value) =>
                    handleChange(`billingInvoice[${index}].email`, value)
                  }
                  error={errors.billingInvoice?.[index]?.email}
                />
                <FormFieldSet
                  title="Phone"
                  type="phone"
                  placeholder="Enter Phone"
                  code={invoice.phone?.code || ""}
                  value={invoice.phone?.number || ""}
                  onChange={(fullValue, details) => {
                    handleChange(`billingInvoice[${index}].phone`, {
                      code: details.countryCode,
                      number: details.number,
                    });
                  }}
                  error={errors.billingInvoice?.[index]?.phone}
                />
              </>
            )}
          />
        </DetailsWrapper>
      </div>
      <div className="flex gap-3 justify-end">
        <Button title="Cancel" variant="outlined" onClick={onClose} />
        <Button title="Save" onClick={handleSave} />
      </div>
    </div>
  );
};

export default EditClientDetails;
